import React, { useState, useEffect } from 'react';
import Web3 from 'web3';
import { NFTABI } from './abis.js';
import CodebaseLicensingProcessorV2ABI from 'abis/NFTLicenseMarketplaceMultipleCoin';
import { message } from 'antd';
import { updateCodeBase } from 'redux/actions/daoUI/codebase.action';
import { createNFTLicense } from 'helpers/nftLicense.api';

const NFTLicensesTab = ({ codebase, chainId, daoInfo }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
      name: '',
      bountyId: '',
      daoId: '',
      challengeId: '',
      banner: '',
      thumbnail: '',
      urlInformation: '',
      devTeamId: '',
      shortDescription: '',
      description: '',
      slug: '',
      nftContractAddress: '',
      nftTitle: '',
      nftDescription: '',
      nftMarketPlaceAddress: '',
  });

  // Multi-dev partner state
  const [devPartners, setDevPartners] = useState([]);
  const [currentDevPartnerPercentage, setCurrentDevPartnerPercentage] = useState(20);
  const [oldDevPartnerPercentage, setOldDevPartnerPercentage] = useState(10);
  const [lpHolderPercentage, setLpHolderPercentage] = useState(45);
  const [daoMembersPercentage, setDaoMembersPercentage] = useState(25);
  const [editingDevPartner, setEditingDevPartner] = useState(null);
  const [devPartnerFormOpen, setDevPartnerFormOpen] = useState(false);
  const [devPartnerFormData, setDevPartnerFormData] = useState({ address: '', isCurrent: false });
  const [devPartnerFormLoading, setDevPartnerFormLoading] = useState(false);
  const [settingCurrentLoading, setSettingCurrentLoading] = useState(null);
  const [confirmDeleteDevPartnerLoading, setConfirmDeleteDevPartnerLoading] = useState(false);
  const [confirmDeleteDevPartner, setConfirmDeleteDevPartner] = useState(null);
  const [confirmDeleteDevPartnerOpen, setConfirmDeleteDevPartnerOpen] = useState(false);
  const [updatingRevenue, setUpdatingRevenue] = useState(false);
  const [loadingMint, setLoadingMint] = useState(false);
  const [loadingLoadData, setLoadingLoadData] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingCompleteTransaction, setLoadingCompleteTransaction] = useState(false);
  const [completionTimeDays, setCompletionTimeDays] = useState(0);
  const [autoCompletionEnabled, setAutoCompletionEnabled] = useState(true);
  const [updatingCompletionTime, setUpdatingCompletionTime] = useState(false);

  // Currency management state
  const [currencyRows, setCurrencyRows] = useState([]);
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmDeleteLoading, setConfirmDeleteLoading] = useState(false);

  const MinimalERC20ABI = [
    {
      inputs: [],
      name: 'symbol',
      outputs: [{ internalType: 'string', name: '', type: 'string' }],
      stateMutability: 'view',
      type: 'function',
    },
    {
      inputs: [],
      name: 'decimals',
      outputs: [{ internalType: 'uint8', name: '', type: 'uint8' }],
      stateMutability: 'view',
      type: 'function',
    },
  ];
  const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

  // Helpers for currency metadata
  const normalize = (a) => ((a || '').toLowerCase() === '0x0' ? ZERO_ADDRESS : (a || '').toLowerCase());
  const normalizeAddress = (addr) => {
    if (!addr) return '';
    const lower = (addr || '').toLowerCase();
    if (lower === '0x0') return ZERO_ADDRESS;
    return lower;
  };

  const getNativeSymbol = (chainId) => {
    const id = Number(chainId);
    if (id === 56) return 'BNB';
    if (id === 137 || id === 80001) return 'MATIC';
    if (id === 7001) return 'ZETA';
    return 'ETH';
  };

  const fetchTokenMeta = async (addr, chainId) => {
    const key = normalize(addr);
    if (key === ZERO_ADDRESS) {
      return { symbol: getNativeSymbol(chainId), decimals: 18 };
    }
    const web3 = new Web3(window.ethereum);
    const token = new web3.eth.Contract(MinimalERC20ABI, key);
    let symbol = '';
    let decimals = 18;
    try {
      symbol = await token.methods.symbol().call();
    } catch (_) {}
    try {
      decimals = await token.methods.decimals().call();
    } catch (_) {}
    return { symbol, decimals: Number(decimals || 18) };
  };

  const toBaseUnit = (amount, decimals) => {
    const d = Number(decimals || 18);
    const [intPart = '0', fracPart = ''] = String(amount || '0').split('.');
    const fracPadded = (fracPart + '0'.repeat(d)).slice(0, d);
    const combined = (intPart.replace(/\D/g, '') || '0') + fracPadded;
    return combined.replace(/^0+(\d)/, '$1') || '0';
  };

  useEffect(() => {
    if (codebase?._id) {
      fetchLicenseData();
    }
  }, [codebase]);

  // Auto-load data when nftContractAddress is available (similar to NFTLicenses.js)
  useEffect(() => {
    if (formData?.nftContractAddress) {
      handleLoadData();
    }
  }, [formData?.nftContractAddress]);

  useEffect(() => {
    if (formData?.supportedCurrencies && formData?.currencyPrices && formData?.currencySymbols && formData?.currencyDecimals) {
      const rows = (formData.supportedCurrencies || []).map((addr) => {
        const key = (addr || '').toLowerCase();
        return {
          address: addr,
          price: formData.currencyPrices?.[key] || '',
          symbol: formData.currencySymbols?.[key] || '',
          decimals: formData.currencyDecimals?.[key] || 18,
          isEditing: false,
          deleted: false,
          loadingSave: false,
          loadingDelete: false,
          errors: {},
        };
      });
      setCurrencyRows(rows);
    }
  }, [formData?.supportedCurrencies, formData?.currencyPrices, formData?.currencySymbols, formData?.currencyDecimals]);

  const fetchLicenseData = async () => {
    setLoading(true);
    try {
      // Mock data for now
      setFormData({
        name: codebase?.name || '',
        bountyId: codebase?.bountyId || '',
        daoId: codebase?.daoId || '',
        challengeId: codebase?.challengeId || '',
        banner: codebase?.banner || '',
        thumbnail: codebase?.thumbnail || '',
        urlInformation: codebase?.urlInformation || '',
        devTeamId: codebase?.devTeamId || '',
        shortDescription: codebase?.shortDescription || '',
        description: codebase?.description || '',
        slug: codebase?.slug || '',
        nftContractAddress: codebase?.nftContractAddress || '',
        nftTitle: codebase?.nftTitle || '',
        nftDescription: codebase?.nftDescription || '',
        nftMarketPlaceAddress: codebase?.nftMarketPlaceAddress || '',
      });
    } catch (error) {
      console.error('Failed to fetch NFT license data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (e) => {
    const value = e?.target?.value;
    setFormData(prev => ({
      ...prev,
      [field]: value !== undefined && value !== null ? value : ''
    }));
  };

  const handleSave = async () => {
    try {
      // TODO: Replace with actual API call
      // await client.api(`/codebases/${codebase.id}/nft-license`, 'put', formData);
      console.log('NFT license data saved successfully');
    } catch (error) {
      console.error('Failed to save NFT license data:', error);
    }
  };

  const handleLoadData = async () => {
    setLoadingLoadData(true);
    try {
      const nftContractAddress = formData.nftContractAddress || codebase.nftContractAddress;
      const nftContracMarket = formData.nftMarketPlaceAddress || codebase.nftMarketPlaceAddress;
      const daoID = formData.daoId || codebase.daoId;

      if (daoID) {
        if (nftContractAddress) {
          const fetchData = await getContractData(nftContractAddress, nftContracMarket, daoID);
          // Load dev partners data
          if (nftContracMarket) {
            await loadDevPartners();
          }
        } else {
          message.error('Please enter a valid NFT contract address.');
        }
      } else {
        message.error('Please select a valid DAO.');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      message.error('Failed to load data. Please try again.');
    } finally {
      setLoadingLoadData(false);
    }
  };

  const getContractData = async (nftContractAddress, nftMarketPlaceAddress, daoID) => {
    const isMetaMaskLoggedIn = await checkMetaMaskLogin();
    if (!isMetaMaskLoggedIn) {
      //console.error('Cannot perform the task as you are not logged into MetaMask.');
      return null;
    }

    const web3 = new Web3(window.ethereum);
    const currentChainId = await web3.eth.getChainId();
    const desiredChainId = daoInfo?.chainId || chainId;

    if (currentChainId.toString() !== desiredChainId.toString()) {
      //console.error(`Please switch to the correct network with chain ID: ${desiredChainId}`);

      try {
        // Yêu cầu người dùng chuyển mạng
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: web3.utils.toHex(desiredChainId) }], // chainId phải là mã hex
        });
      } catch (switchError) {
        if (switchError.code === 4902) {
          // Nếu mạng không tồn tại trong danh sách, bạn có thể thêm nó. Lưu ý rằng bạn cần cung cấp nhiều thông tin hơn về mạng.
          try {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [
                {
                  chainId: web3.utils.toHex(desiredChainId),
                  // Các thông tin khác như chainName, nativeCurrency, rpcUrls, blockExplorerUrls, ...
                },
              ],
            });
          } catch (addError) {
            //console.error('Unable to add the chain:', addError);
          }
        } else {
          //console.error('Unable to switch the chain:', switchError);
        }
      }

      return null;
    }

    const contractNFT = new web3.eth.Contract(NFTABI, nftContractAddress);
    const contractMarketNFT = new web3.eth.Contract(CodebaseLicensingProcessorV2ABI, nftMarketPlaceAddress);

    try {
      const nftTitle = await contractNFT.methods.name().call();
      
      // Get all supported currencies and their prices
      const supportedCurrencies = await contractMarketNFT.methods.getSupportedCurrencies().call();
      const completionTimeSeconds = await contractMarketNFT.methods.completionTime().call();
      const autoEnabled = await contractMarketNFT.methods.isAutoCompletionEnabled().call();
      const currentChainId = daoInfo?.chainId || chainId;
      const prices = {};
      const decimals = {};
      const symbols = {};
      
      for (let i = 0; i < supportedCurrencies.length; i++) {
        const addr = normalize(supportedCurrencies[i]);
        const meta = await fetchTokenMeta(addr, currentChainId);
        symbols[addr] = meta.symbol || '';
        decimals[addr] = Number(meta.decimals || 18);
        try {
          const p = await contractMarketNFT.methods.getPriceForCurrency(addr).call();
          const denom = Math.pow(10, Number(decimals[addr] || 18));
          prices[addr] = (Number(p || '0') / denom).toString();
        } catch (e) {
          prices[addr] = '0';
        }
      }

      const initData = {
        ...formData,
        nftContractAddress: nftContractAddress || formData?.nftContractAddress || '',
        nftTitle: nftTitle || '',
        nftDescription: formData?.nftDescription || codebase?.nftDescription || '',
        daoId: daoID || formData?.daoId || '',
        nftMarketPlaceAddress: nftMarketPlaceAddress || formData?.nftMarketPlaceAddress || '',
        supportedCurrencies: supportedCurrencies,
        currencyPrices: prices,
        currencySymbols: symbols,
        currencyDecimals: decimals,
      };

      setFormData(initData);
      setCompletionTimeDays(Number(completionTimeSeconds) / (24 * 60 * 60));
      setAutoCompletionEnabled(Boolean(autoEnabled));
      return true;
    } catch (error) {
      console.error('Error while fetching contract data:', error);
      message.error('Failed to fetch contract data.');
      return false;
    }
  };

  const checkMetaMaskLogin = async () => {
    if (!window.ethereum) {
      // console.error('Trình duyệt không hỗ trợ Ethereum (MetaMask)');
      return false;
    }

    try {
      await window.ethereum.request({ method: 'eth_requestAccounts' });
      // Người dùng đã đăng nhập vào MetaMask
      return true;
    } catch (error) {
      // Người dùng từ chối đăng nhập hoặc có lỗi
      // console.error('Không thể đăng nhập vào MetaMask:', error);
      return false;
    }
  };

  const handleMint = async () => {
    const nftContractAddress = formData.nftContractAddress;
    const nftContracMarket = formData.nftMarketPlaceAddress;
    const daoID = formData.daoId;

    if (!nftContractAddress) {
      message.error('Please enter a valid NFT contract address.');
      return;
    }

    try {
      setLoadingMint(true);
      // 1. Mint NFT on chain
      const mintResult = await mintNewNFT(nftContractAddress, nftContracMarket, daoID);

      if (mintResult.success) {
        // 2. Save NFT License to database
        // get term of license in years
        const web3 = new Web3(window.ethereum);
        const contractMarketNFT = new web3.eth.Contract(CodebaseLicensingProcessorV2ABI, nftContracMarket);
        const termOfLicenseinYears = await contractMarketNFT.methods.termOfLicenseinYears().call();

        const nftLicenseData = {
          codebaseId: codebase?._id,
          tokenId: mintResult.tokenId || 0,
          contractAddress: nftContractAddress,
          marketplaceAddress: nftContracMarket,
          licenseTerm: parseInt(termOfLicenseinYears) || 1,
          mintTransactionHash: mintResult.transactionHash,
          price: parseFloat(formData.nftPrice) || 0,
          currency: formData.currencyAddress ? formData.currencyAddress : 'KAT',
          currencyAddress: formData.currencyAddress,
          unlockableContent: formData.nftDescription,
          notes: `Minted via user panel for ${formData.nftTitle}`
        };

        await createNFTLicense(nftLicenseData);
        message.success('NFT minted successfully!');
      } else {
        message.error('Failed to mint NFT. Please try again.');
      }
    } catch (error) {
      console.error('Failed to mint NFT:', error);
      message.error('Failed to mint NFT. Please try again.');
    } finally {
      setLoadingMint(false);
    }
  };

  const mintNewNFT = async (nftContractAddress, nftMarketPlaceAddress, daoID) => {
    const isMetaMaskLoggedIn = await checkMetaMaskLogin();
    if (!isMetaMaskLoggedIn) {
      return {
        success: false,
        error: 'Please log in to MetaMask to proceed.',
      };
    }

    const web3 = new Web3(window.ethereum);
    const currentChainId = await web3.eth.getChainId();
    const desiredChainId = daoInfo?.chainId || chainId;

    if (currentChainId.toString() !== desiredChainId?.toString()) {
      try {
        // Yêu cầu người dùng chuyển mạng
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: web3.utils.toHex(desiredChainId) }], // chainId phải là mã hex
        });
      } catch (switchError) {
        if (switchError.code === 4902) {
          try {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [
                {
                  chainId: web3.utils.toHex(desiredChainId),
                  // Các thông tin khác như chainName, nativeCurrency, rpcUrls, blockExplorerUrls, ...
                },
              ],
            });
          } catch (addError) {
            return {
              success: false,
              error: 'Unable to add the chain to MetaMask.',
            };
          }
        } else {
          return {
            success: false,
            error: 'Unable to switch to the correct network.',
          };
        }
      }
    }

    const contractNFT = new web3.eth.Contract(NFTABI, nftContractAddress);

    try {
      const accounts = await web3.eth.getAccounts();

      // Lấy gas price từ chain và tăng 10%
      const gasPrice = await web3.eth.getGasPrice();
      const aggressiveGasPrice = web3.utils.toBN(gasPrice).mul(web3.utils.toBN(11)).div(web3.utils.toBN(10)); // gasPrice * 1.1

      // Gửi giao dịch mint NFT
      let transactionHash = null;
      let tokenId = null;

      const result = await contractNFT.methods.mint(daoInfo.gtTokenAddress).send({
        from: accounts[0],
        gasPrice: aggressiveGasPrice.toString(), // Truyền gas price đã tăng 10%
      })
      .on('transactionHash', (hash) => {
        transactionHash = hash;
      })
      .on('receipt', (receipt) => {
        if (receipt.events && receipt.events.Transfer) {
          tokenId = receipt.events.Transfer.returnValues.tokenId;
        }
      });

      return {
        success: true,
        transactionHash: transactionHash,
        tokenId: tokenId,
        receipt: result
      };
    } catch (error) {
      console.error('Error while minting new token:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  };

  const handleIssueNFT = async () => {
    const nftContractAddress = formData.nftContractAddress;
    const nftContracMarket = formData.nftMarketPlaceAddress;
    const productionPartnerAddress = formData.productionPartnerAddress;
    const daoID = formData.daoId;
    if (nftContractAddress) {
      await issueCodebase(nftContractAddress, nftContracMarket, daoID, productionPartnerAddress);
    }
  };

  const issueCodebase = async (nftContractAddress, nftMarketPlaceAddress, daoID, productionPartnerAddress) => {
    const isMetaMaskLoggedIn = await checkMetaMaskLogin();
    if (!isMetaMaskLoggedIn) {
      return null;
    }

    const web3 = new Web3(window.ethereum);
    const currentChainId = await web3.eth.getChainId();
    const desiredChainId = daoInfo.chainId;

    if (currentChainId.toString() !== desiredChainId) {
      try {
        // Yêu cầu người dùng chuyển mạng
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: web3.utils.toHex(desiredChainId) }], // chainId phải là mã hex
        });
      } catch (switchError) {
        if (switchError.code === 4902) {
          // Nếu mạng không tồn tại trong danh sách, thêm mạng mới
          try {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [
                {
                  chainId: web3.utils.toHex(desiredChainId),
                  // Các thông tin khác như chainName, nativeCurrency, rpcUrls, blockExplorerUrls, ...
                },
              ],
            });
          } catch (addError) {
            return null;
          }
        } else {
          return null;
        }
      }
    }

    const contractNFT = new web3.eth.Contract(NFTABI, nftContractAddress);

    try {
      const accounts = await web3.eth.getAccounts();

      // Lấy gas price từ chain và tăng 10%
      const gasPrice = await web3.eth.getGasPrice();
      const aggressiveGasPrice = web3.utils.toBN(gasPrice).mul(web3.utils.toBN(11)).div(web3.utils.toBN(10)); // gasPrice * 1.1

      // Gửi giao dịch issue NFT
      await contractNFT.methods.issueNFT(productionPartnerAddress).send({
        from: accounts[0],
        gasPrice: aggressiveGasPrice.toString(), // Truyền gas price đã tăng 10%
      });

      return true;
    } catch (error) {
      console.error('Error while issuing NFT:', error);
      return false;
    }
  };

  const handleRevoke = async () => {
    const nftContractAddress = formData.nftContractAddress;
    const nftContracMarket = formData.nftMarketPlaceAddress;
    const productionPartnerAddress = formData.productionPartnerAddress;
    const daoID = formData.daoId;
    if (nftContractAddress) {
      const issue = await revokeCodebase(nftContractAddress, nftContracMarket, daoID, productionPartnerAddress);
    } else {
      // Handle the case when nftContractAddress is empty
    }
  };

  const revokeCodebase = async (nftContractAddress, nftMarketPlaceAddress, daoID, productionPartnerAddress) => {
    const isMetaMaskLoggedIn = await checkMetaMaskLogin();
    if (!isMetaMaskLoggedIn) {
      return null;
    }

    const web3 = new Web3(window.ethereum);
    const currentChainId = await web3.eth.getChainId();
    const desiredChainId = daoInfo.chainId;

    if (currentChainId.toString() !== desiredChainId) {
      try {
        // Yêu cầu người dùng chuyển mạng
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: web3.utils.toHex(desiredChainId) }], // chainId phải là mã hex
        });
      } catch (switchError) {
        if (switchError.code === 4902) {
          // Nếu mạng không tồn tại trong danh sách, thêm mạng mới
          try {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [
                {
                  chainId: web3.utils.toHex(desiredChainId),
                  // Các thông tin khác như chainName, nativeCurrency, rpcUrls, blockExplorerUrls, ...
                },
              ],
            });
          } catch (addError) {
            return null;
          }
        } else {
          return null;
        }
      }
    }

    const contractNFT = new web3.eth.Contract(NFTABI, nftContractAddress);

    try {
      const accounts = await web3.eth.getAccounts();

      // Lấy gas price từ chain và tăng 10%
      const gasPrice = await web3.eth.getGasPrice();
      const aggressiveGasPrice = web3.utils.toBN(gasPrice).mul(web3.utils.toBN(11)).div(web3.utils.toBN(10)); // gasPrice * 1.1
      // Gửi giao dịch revoke NFT
      await contractNFT.methods.revoke(productionPartnerAddress).send({
        from: accounts[0],
        gasPrice: aggressiveGasPrice.toString(), // Truyền gas price đã tăng 10%
      });

      return true;
    } catch (error) {
      console.error('Error while revoking NFT:', error);
      return false;
    }
  };


  const handleCompleteTransaction = async () => {
    setLoadingCompleteTransaction(true);
    try {
      const tokenId = formData?.tokenId
      const nftMarketPlaceAddress = formData.nftMarketPlaceAddress;

      // Kiểm tra đăng nhập MetaMask
      const isMetaMaskLoggedIn = await checkMetaMaskLogin();
      if (!isMetaMaskLoggedIn) {
        message.warning('Please log in to MetaMask to proceed.');
        setLoadingCompleteTransaction(false);
        return null;
      }

      const web3 = new Web3(window.ethereum);
      const currentChainId = await web3.eth.getChainId();
      const desiredChainId = daoInfo.chainId;

      // Kiểm tra mạng
      if (currentChainId.toString() !== desiredChainId) {
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: web3.utils.toHex(desiredChainId) }],
          });
        } catch (switchError) {
          if (switchError.code === 4902) {
            try {
              await window.ethereum.request({
                method: 'wallet_addEthereumChain',
                params: [
                  {
                    chainId: web3.utils.toHex(desiredChainId),
                    // Thêm thông tin mạng nếu cần
                  },
                ],
              });
            } catch (addError) {
              message.error('Unable to add the chain to MetaMask.');
              setLoadingCompleteTransaction(false);
              return null;
            }
          } else {
            message.error('Unable to switch to the correct network.');
            setLoadingCompleteTransaction(false);
            return null;
          }
        }
      }

      const contractMarketNFT = new web3.eth.Contract(CodebaseLicensingProcessorV2ABI, nftMarketPlaceAddress);

      const accounts = await web3.eth.getAccounts();

      // Lấy gas price từ chain và tăng 10%
      const gasPrice = await web3.eth.getGasPrice();
      const aggressiveGasPrice = web3.utils.toBN(gasPrice).mul(web3.utils.toBN(11)).div(web3.utils.toBN(10)); // gasPrice * 1.1

      // Gửi giao dịch completeTransaction
      await contractMarketNFT.methods.completeTransaction(tokenId).send({
        from: accounts[0],
        gasPrice: aggressiveGasPrice.toString(),
      });

      message.success('Transaction completed successfully.');
      return true;
    } catch (error) {
      console.error('Error while completing transaction:', error);
      if (error.message?.includes('User rejected') || error.code === 4001) {
        message.warning('Transaction was rejected.');
      } else {
        message.error('Failed to complete the transaction: ' + (error.message || 'Unknown error'));
      }
      return false;
    } finally {
      setLoadingCompleteTransaction(false);
    }
  };

  const buttonStyle = {
    padding: '6px 12px',
    borderRadius: 4,
    border: '1px solid #1976d2',
    background: '#fff',
    color: '#1976d2',
    cursor: 'pointer'
  };

  const inputStyle = {
    width: '100%',
    padding: 10,
    marginTop: 6,
    border: '1px solid #ccc',
    borderRadius: 6,
    background: '#fff'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: 4,
    fontWeight: 600
  };

  // Multi-dev partner management functions
  const loadDevPartners = async () => {
    if (!formData?.nftMarketPlaceAddress) return;

    try {
      const web3 = new Web3(window.ethereum);
      const contractMarketNFT = new web3.eth.Contract(CodebaseLicensingProcessorV2ABI, formData.nftMarketPlaceAddress);

      const [partners, currentPercentage, oldPercentage, lpPct, daoPct, completionTimeSeconds] = await Promise.all([
        contractMarketNFT.methods.getDevPartners().call(),
        contractMarketNFT.methods.currentDevPartnerPercentage().call(),
        contractMarketNFT.methods.oldDevPartnerPercentage().call(),
        contractMarketNFT.methods.LPHolderPercentage().call(),
        contractMarketNFT.methods.DAOMembersPercentage().call(),
        contractMarketNFT.methods.completionTime().call(),
      ]);

      setDevPartners(partners);
      setCurrentDevPartnerPercentage(Number(currentPercentage));
      setOldDevPartnerPercentage(Number(oldPercentage));
      setLpHolderPercentage(Number(lpPct));
      setDaoMembersPercentage(Number(daoPct));
      // Convert seconds to days (completionTime is stored in seconds)
      setCompletionTimeDays(Number(completionTimeSeconds) / (24 * 60 * 60));
    } catch (error) {
      console.error('Error loading dev partners:', error);
      // Silently fail - might be V1 contract
    }
  };

  const addDevPartner = async (address, isCurrent) => {
    try {
      const web3 = new Web3(window.ethereum);
      const currentChainId = await web3.eth.getChainId();
      const desiredChainId = daoInfo?.chainId || chainId;

      if (currentChainId.toString() !== desiredChainId?.toString()) {
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: web3.utils.toHex(desiredChainId) }],
          });
        } catch (switchError) {
          if (switchError.code === 4902) {
            try {
              await window.ethereum.request({
                method: 'wallet_addEthereumChain',
                params: [{ chainId: web3.utils.toHex(desiredChainId) }],
              });
            } catch (addError) {
              message.error('Unable to add the chain to MetaMask.');
              return false;
            }
          } else {
            message.error('Unable to switch to the correct network.');
            return false;
          }
        }
      }

      const contractMarketNFT = new web3.eth.Contract(CodebaseLicensingProcessorV2ABI, formData.nftMarketPlaceAddress);
      const accounts = await web3.eth.getAccounts();
      const gasPrice = await web3.eth.getGasPrice();
      const aggressiveGasPrice = web3.utils.toBN(gasPrice).mul(web3.utils.toBN(11)).div(web3.utils.toBN(10));

      await contractMarketNFT.methods.addDevPartner(address, isCurrent).send({
        from: accounts[0],
        gasPrice: aggressiveGasPrice.toString(),
      });

      await loadDevPartners();
      message.success('Dev partner added successfully.');
      return true;
    } catch (error) {
      console.error('Error adding dev partner:', error);
      message.error('Failed to add dev partner.');
      return false;
    }
  };

  const removeDevPartner = async (address) => {
    try {
      const web3 = new Web3(window.ethereum);
      const currentChainId = await web3.eth.getChainId();
      const desiredChainId = daoInfo?.chainId || chainId;

      if (currentChainId.toString() !== desiredChainId?.toString()) {
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: web3.utils.toHex(desiredChainId) }],
          });
        } catch (switchError) {
          if (switchError.code === 4902) {
            try {
              await window.ethereum.request({
                method: 'wallet_addEthereumChain',
                params: [{ chainId: web3.utils.toHex(desiredChainId) }],
              });
            } catch (addError) {
              message.error('Unable to add the chain to MetaMask.');
              return false;
            }
          } else {
            message.error('Unable to switch to the correct network.');
            return false;
          }
        }
      }

      const contractMarketNFT = new web3.eth.Contract(CodebaseLicensingProcessorV2ABI, formData.nftMarketPlaceAddress);
      const accounts = await web3.eth.getAccounts();
      const gasPrice = await web3.eth.getGasPrice();
      const aggressiveGasPrice = web3.utils.toBN(gasPrice).mul(web3.utils.toBN(11)).div(web3.utils.toBN(10));

      await contractMarketNFT.methods.removeDevPartner(address).send({
        from: accounts[0],
        gasPrice: aggressiveGasPrice.toString(),
      });

      await loadDevPartners();
      message.success('Dev partner removed successfully.');
      return true;
    } catch (error) {
      console.error('Error removing dev partner:', error);
      message.error('Failed to remove dev partner.');
      return false;
    }
  };

  const setCurrentDevPartner = async (address) => {
    try {
      const web3 = new Web3(window.ethereum);
      const currentChainId = await web3.eth.getChainId();
      const desiredChainId = daoInfo?.chainId || chainId;

      if (currentChainId.toString() !== desiredChainId?.toString()) {
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: web3.utils.toHex(desiredChainId) }],
          });
        } catch (switchError) {
          if (switchError.code === 4902) {
            try {
              await window.ethereum.request({
                method: 'wallet_addEthereumChain',
                params: [{ chainId: web3.utils.toHex(desiredChainId) }],
              });
            } catch (addError) {
              message.error('Unable to add the chain to MetaMask.');
              return false;
            }
          } else {
            message.error('Unable to switch to the correct network.');
            return false;
          }
        }
      }

      const contractMarketNFT = new web3.eth.Contract(CodebaseLicensingProcessorV2ABI, formData.nftMarketPlaceAddress);
      const accounts = await web3.eth.getAccounts();
      const gasPrice = await web3.eth.getGasPrice();
      const aggressiveGasPrice = web3.utils.toBN(gasPrice).mul(web3.utils.toBN(11)).div(web3.utils.toBN(10));

      await contractMarketNFT.methods.setCurrentDevPartner(address).send({
        from: accounts[0],
        gasPrice: aggressiveGasPrice.toString(),
      });

      await loadDevPartners();
      message.success('Current dev partner updated successfully.');
      return true;
    } catch (error) {
      console.error('Error setting current dev partner:', error);
      message.error('Failed to update current dev partner.');
      return false;
    }
  };

  const updateRevenuePercentagesOnChain = async (current, old, lp, dao) => {
    const total = Number(current) + Number(old) + Number(lp) + Number(dao);
    if (total !== 100) {
      message.error('Total percentages must equal 100%.');
      return false;
    }
    try {
      setUpdatingRevenue(true);
      const web3 = new Web3(window.ethereum);
      const currentChainId = await web3.eth.getChainId();
      const desiredChainId = daoInfo?.chainId || chainId;

      if (currentChainId.toString() !== desiredChainId?.toString()) {
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: web3.utils.toHex(desiredChainId) }],
          });
        } catch (switchError) {
          if (switchError.code === 4902) {
            try {
              await window.ethereum.request({
                method: 'wallet_addEthereumChain',
                params: [{ chainId: web3.utils.toHex(desiredChainId) }],
              });
            } catch (addError) {
              message.error('Unable to add the chain to MetaMask.');
              return false;
            }
          } else {
            message.error('Unable to switch to the correct network.');
            return false;
          }
        }
      }

      const contractMarketNFT = new web3.eth.Contract(CodebaseLicensingProcessorV2ABI, formData.nftMarketPlaceAddress);
      const accounts = await web3.eth.getAccounts();
      const gasPrice = await web3.eth.getGasPrice();
      const aggressiveGasPrice = web3.utils.toBN(gasPrice).mul(web3.utils.toBN(11)).div(web3.utils.toBN(10));

      await contractMarketNFT.methods.updateRevenuePercentages(current, old, lp, dao).send({
        from: accounts[0],
        gasPrice: aggressiveGasPrice.toString(),
      });

      await loadDevPartners();
      message.success('Revenue percentages updated successfully.');
      return true;
    } catch (error) {
      console.error('Error updating revenue percentages:', error);
      message.error('Failed to update revenue percentages.');
      return false;
    } finally {
      setUpdatingRevenue(false);
    }
  };

  const updateCompletionTimeOnChain = async (days) => {
    if (Number(days) <= 0) {
      message.error('Completion time must be greater than 0 days.');
      return false;
    }
    try {
      setUpdatingCompletionTime(true);
      const web3 = new Web3(window.ethereum);
      const currentChainId = await web3.eth.getChainId();
      const desiredChainId = daoInfo?.chainId || chainId;

      if (currentChainId.toString() !== desiredChainId?.toString()) {
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: web3.utils.toHex(desiredChainId) }],
          });
        } catch (switchError) {
          if (switchError.code === 4902) {
            try {
              await window.ethereum.request({
                method: 'wallet_addEthereumChain',
                params: [{ chainId: web3.utils.toHex(desiredChainId) }],
              });
            } catch (addError) {
              message.error('Unable to add the chain to MetaMask.');
              return false;
            }
          } else {
            message.error('Unable to switch to the correct network.');
            return false;
          }
        }
      }

      const contractMarketNFT = new web3.eth.Contract(CodebaseLicensingProcessorV2ABI, formData.nftMarketPlaceAddress);
      const accounts = await web3.eth.getAccounts();
      const gasPrice = await web3.eth.getGasPrice();
      const aggressiveGasPrice = web3.utils.toBN(gasPrice).mul(web3.utils.toBN(11)).div(web3.utils.toBN(10));

      await contractMarketNFT.methods.updateCompletionTime(Number(days)).send({
        from: accounts[0],
        gasPrice: aggressiveGasPrice.toString(),
      });

      await loadDevPartners();
      message.success('Completion time updated successfully.');
      return true;
    } catch (error) {
      console.error('Error updating completion time:', error);
      message.error('Failed to update completion time.');
      return false;
    } finally {
      setUpdatingCompletionTime(false);
    }
  };

  // Dev partner form handlers
  const handleAddDevPartner = () => {
    setDevPartnerFormData({ address: '', isCurrent: false });
    setEditingDevPartner(null);
    setDevPartnerFormOpen(true);
  };

  const handleEditDevPartner = (partner) => {
    setDevPartnerFormData({ address: partner.wallet, isCurrent: partner.isCurrent });
    setEditingDevPartner(partner);
    setDevPartnerFormOpen(true);
  };

  const handleSaveDevPartner = async () => {
    if (!devPartnerFormData.address) {
      message.error('Please enter a valid address.');
      return;
    }

    setDevPartnerFormLoading(true);
    try {
      if (editingDevPartner) {
        // For editing, we need to remove and re-add
        await removeDevPartner(editingDevPartner.wallet);
        await addDevPartner(devPartnerFormData.address, devPartnerFormData.isCurrent);
      } else {
        await addDevPartner(devPartnerFormData.address, devPartnerFormData.isCurrent);
      }

      setDevPartnerFormOpen(false);
      setDevPartnerFormData({ address: '', isCurrent: false });
      setEditingDevPartner(null);
    } catch (error) {
      console.error('Error saving dev partner:', error);
      message.error('Error saving dev partner. Please try again.');
    } finally {
      setDevPartnerFormLoading(false);
    }
  };

  const handleDeleteDevPartner = (address) => {
    setConfirmDeleteDevPartner(address);
    setConfirmDeleteDevPartnerOpen(true);
  };

  const handleConfirmDeleteDevPartner = async () => {
    if (confirmDeleteDevPartner) {
      setConfirmDeleteDevPartnerLoading(true);
      try {
        await removeDevPartner(confirmDeleteDevPartner);
        setConfirmDeleteDevPartnerOpen(false);
        setConfirmDeleteDevPartner(null);
      } catch (error) {
        console.error('Error deleting dev partner:', error);
        message.error('Error deleting dev partner. Please try again.');
      } finally {
        setConfirmDeleteDevPartnerLoading(false);
      }
    }
  };

  const handleSetCurrentDevPartner = async (address) => {
    setSettingCurrentLoading(address);
    try {
      await setCurrentDevPartner(address);
    } catch (error) {
      console.error('Error setting current dev partner:', error);
      message.error('Error setting current dev partner. Please try again.');
    } finally {
      setSettingCurrentLoading(null);
    }
  };

  const ensureMarketplaceContract = async () => {
    const isMetaMaskLoggedIn = await checkMetaMaskLogin();
    if (!isMetaMaskLoggedIn) {
      message.error('Please log in to MetaMask to proceed.');
      return null;
    }

    const web3 = new Web3(window.ethereum);
    const currentChainId = await web3.eth.getChainId();
    const desiredChainId = daoInfo?.chainId || chainId;

    if (currentChainId.toString() !== desiredChainId?.toString()) {
      try {
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: web3.utils.toHex(desiredChainId) }],
        });
      } catch (switchError) {
        if (switchError.code === 4902) {
          try {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [{ chainId: web3.utils.toHex(desiredChainId) }],
            });
          } catch (addError) {
            message.error('Unable to add the chain to MetaMask.');
            return null;
          }
        } else {
          message.error('Unable to switch to the correct network.');
          return null;
        }
      }
    }

    const accounts = await web3.eth.getAccounts();
    if (!accounts || accounts.length === 0) {
      message.error('No accounts found.');
      return null;
    }

    const contractMarketNFT = new web3.eth.Contract(CodebaseLicensingProcessorV2ABI, formData?.nftMarketPlaceAddress);
    const averageGasPrice = await web3.eth.getGasPrice();
    const aggressiveGasPrice = web3.utils.toBN(averageGasPrice).muln(15).divn(10);

    return { web3, account: accounts[0], contractMarketNFT, aggressiveGasPrice };
  };

  // Currency table handlers
  const handleAddCurrencyRow = () => {
    setCurrencyRows([
      ...currencyRows,
      {
        address: '',
        price: '',
        symbol: '',
        decimals: 18,
        isEditing: true,
        deleted: false,
        loadingSave: false,
        loadingDelete: false,
        errors: {},
        _backup: { address: '', price: '' },
      },
    ]);
  };

  const handleRowAddressChange = (rowIndex, value) => {
    const next = [...currencyRows];
    next[rowIndex] = {
      ...next[rowIndex],
      address: value,
      errors: { ...(next[rowIndex].errors || {}), address: '' },
    };
    setCurrencyRows(next);
  };

  const handleRowPriceChange = (rowIndex, value) => {
    const next = [...currencyRows];
    next[rowIndex] = {
      ...next[rowIndex],
      price: value,
      errors: { ...(next[rowIndex].errors || {}), price: '' },
    };
    setCurrencyRows(next);
  };

  const handleRowEdit = (rowIndex) => {
    const row = currencyRows[rowIndex];
    const next = [...currencyRows];
    next[rowIndex] = {
      ...row,
      isEditing: true,
      _backup: { address: row.address, price: row.price },
    };
    setCurrencyRows(next);
  };

  const handleRowCancel = (rowIndex) => {
    const row = currencyRows[rowIndex];
    const next = [...currencyRows];
    const wasNewBlank =
      !row._backup ||
      ((!row._backup.address || row._backup.address === '') && (!row._backup.price || row._backup.price === ''));
    if (wasNewBlank) {
      next.splice(rowIndex, 1);
    } else if (row._backup) {
      next[rowIndex] = {
        ...row,
        address: row._backup.address,
        price: row._backup.price,
        isEditing: false,
        errors: {},
        _backup: undefined,
      };
    } else {
      next[rowIndex] = { ...row, isEditing: false };
    }
    setCurrencyRows(next);
  };

  const handleRowDeleteClick = (rowIndex) => {
    setConfirmDeleteIdx(rowIndex);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    const idx = confirmDeleteIdx;
    if (idx == null) {
      return;
    }

    setConfirmDeleteLoading(true);
    setConfirmDeleteOpen(false);
    const row = currencyRows[idx];
    try {
      setCurrencyRows((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], loadingDelete: true };
        return next;
      });
      const ctx = await ensureMarketplaceContract();
      if (!ctx) return;
      const addrNorm = normalize(row.address);
      await ctx.contractMarketNFT.methods.removeSupportedCurrency(addrNorm).send({
        from: ctx.account,
        gasPrice: ctx.aggressiveGasPrice.toString(),
      });

      const nextRows = [...currencyRows];
      nextRows[idx] = { ...row, deleted: true };
      setCurrencyRows(nextRows);

      setFormData((prev) => {
        const nextSupported = (prev?.supportedCurrencies || []).filter((a) => normalize(a) !== addrNorm);
        const nextPrices = { ...(prev?.currencyPrices || {}) };
        delete nextPrices[addrNorm];
        const nextSymbols = { ...(prev?.currencySymbols || {}) };
        delete nextSymbols[addrNorm];
        const nextDecimals = { ...(prev?.currencyDecimals || {}) };
        delete nextDecimals[addrNorm];
        return {
          ...prev,
          supportedCurrencies: nextSupported,
          currencyPrices: nextPrices,
          currencySymbols: nextSymbols,
          currencyDecimals: nextDecimals,
        };
      });
      message.success('Currency removed successfully.');
    } catch (e) {
      console.error('Failed to remove currency', e);
      message.error('Remove currency failed.');
    } finally {
      setCurrencyRows((prev) => {
        const next = [...prev];
        if (idx != null && next[idx]) next[idx] = { ...next[idx], loadingDelete: false };
        return next;
      });
      setConfirmDeleteIdx(null);
      setConfirmDeleteLoading(false);
    }
  };

  const handleRowSave = async (rowIndex) => {
    const row = currencyRows[rowIndex];
    const trimmedAddress = (row.address || '').trim();
    const priceText = (row.price || '').toString().trim();

    const newErrors = {};
    if (!trimmedAddress) {
      newErrors.address = 'Currency address is required';
    }
    if (!priceText) {
      newErrors.price = 'Price is required';
    } else {
      const priceNumber = Number(priceText);
      if (Number.isNaN(priceNumber) || priceNumber <= 0) {
        newErrors.price = 'Price must be a positive number';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      const next = [...currencyRows];
      next[rowIndex] = { ...row, errors: newErrors };
      setCurrencyRows(next);
      return;
    }

    setCurrencyRows((prev) => {
      const next = [...prev];
      next[rowIndex] = { ...next[rowIndex], loadingSave: true };
      return next;
    });

    const ctx = await ensureMarketplaceContract();
    if (!ctx) return;

    const meta = await fetchTokenMeta(trimmedAddress, daoInfo?.chainId || chainId);

    const isNew =
      !row._backup ||
      ((!row._backup.address || row._backup.address === '') && (!row._backup.price || row._backup.price === ''));
    const oldAddrNorm = normalize(row._backup?.address || '');
    const newAddrNorm = normalize(trimmedAddress);
    const priceWei = toBaseUnit(priceText, meta.decimals);

    try {
      if (isNew) {
        await ctx.contractMarketNFT.methods.addSupportedCurrency(newAddrNorm, priceWei).send({
          from: ctx.account,
          gasPrice: ctx.aggressiveGasPrice.toString(),
        });
        setFormData((prev) => ({
          ...prev,
          supportedCurrencies: [...(prev?.supportedCurrencies || []), newAddrNorm],
          currencySymbols: { ...(prev?.currencySymbols || {}), [newAddrNorm]: meta.symbol },
          currencyDecimals: { ...(prev?.currencyDecimals || {}), [newAddrNorm]: meta.decimals },
          currencyPrices: { ...(prev?.currencyPrices || {}), [newAddrNorm]: priceText },
        }));
      } else {
        const addrChanged = oldAddrNorm !== newAddrNorm;
        const oldPriceText = (formData?.currencyPrices || {})[oldAddrNorm] || row._backup?.price || '';
        const priceChanged = oldPriceText !== priceText;

        if (!addrChanged && !priceChanged) {
          const nextNoChange = [...currencyRows];
          nextNoChange[rowIndex] = { ...row, isEditing: false, errors: {}, _backup: undefined };
          setCurrencyRows(nextNoChange);
          return;
        }

        if (addrChanged) {
          await ctx.contractMarketNFT.methods.removeSupportedCurrency(oldAddrNorm).send({
            from: ctx.account,
            gasPrice: ctx.aggressiveGasPrice.toString(),
          });
          await ctx.contractMarketNFT.methods.addSupportedCurrency(newAddrNorm, priceWei).send({
            from: ctx.account,
            gasPrice: ctx.aggressiveGasPrice.toString(),
          });
          setFormData((prev) => {
            const nextSupported = (prev?.supportedCurrencies || []).filter((a) => normalize(a) !== oldAddrNorm);
            nextSupported.push(newAddrNorm);
            const nextPrices = { ...(prev?.currencyPrices || {}) };
            delete nextPrices[oldAddrNorm];
            nextPrices[newAddrNorm] = priceText;
            const nextSymbols = { ...(prev?.currencySymbols || {}) };
            delete nextSymbols[oldAddrNorm];
            nextSymbols[newAddrNorm] = meta.symbol;
            const nextDecimals = { ...(prev?.currencyDecimals || {}) };
            delete nextDecimals[oldAddrNorm];
            nextDecimals[newAddrNorm] = meta.decimals;
            return {
              ...prev,
              supportedCurrencies: nextSupported,
              currencyPrices: nextPrices,
              currencySymbols: nextSymbols,
              currencyDecimals: nextDecimals,
            };
          });
        } else if (priceChanged) {
          await ctx.contractMarketNFT.methods.updateCurrencyPrice(newAddrNorm, priceWei).send({
            from: ctx.account,
            gasPrice: ctx.aggressiveGasPrice.toString(),
          });
          setFormData((prev) => ({
            ...prev,
            currencyPrices: { ...(prev?.currencyPrices || {}), [newAddrNorm]: priceText },
          }));
        }
      }

      const next = [...currencyRows];
      next[rowIndex] = {
        ...row,
        address: trimmedAddress,
        price: priceText,
        symbol: meta.symbol,
        decimals: meta.decimals,
        isEditing: false,
        errors: {},
        _backup: undefined,
      };
      setCurrencyRows(next);
      message.success('Currency updated successfully.');
    } catch (e) {
      console.error('Failed to save currency row', e);
      message.error('Update currency failed.');
    } finally {
      setCurrencyRows((prev) => {
        const next = [...prev];
        if (next[rowIndex]) next[rowIndex] = { ...next[rowIndex], loadingSave: false };
        return next;
      });
    }
  };

  const validationForm = () => {
    if (!formData?.nftContractAddress) {
      message.error('Contract address must not be empty');
      return true;
    }


    return false;
  };

  const onSubmit = async () => {
    setLoadingSave(true);

    try {
      if (formData?.nftContractAddress && formData?.nftMarketPlaceAddress && formData?.daoId) {
        // Validate form
        if (validationForm()) {
          throw new Error('Form validation failed');
        }

        // Update codebase using the action
        const response = await updateCodeBase({
          codeBaseId: codebase?._id,
          data: formData
        })();

        if (response) {
          message.success('NFT License updated successfully!');
        } else {
          throw new Error('Failed to update codebase');
        }
      } else {
        throw new Error('Please fill in all required fields');
      }
    } catch (error) {
      message.error(error.message || 'Failed to update NFT License');
    } finally {
      setLoadingSave(false);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ marginBottom: '24px', fontWeight: 600 }}>
        NFT License {formData.name ? `(${formData.name})` : '(undefined)'}
      </h2>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>
          Codebase Contract Address <span style={{ color: 'red' }}>*</span>
        </label>
        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
          <input
            value={formData.nftContractAddress}
            onChange={handleInputChange('nftContractAddress')}
            placeholder="Enter contract address"
            style={{ ...inputStyle, fontFamily: 'monospace', flex: 1 }}
          />
          <button
            onClick={handleLoadData}
            style={{
              ...buttonStyle,
              opacity: loadingLoadData ? 0.6 : 1,
              cursor: loadingLoadData ? 'not-allowed' : 'pointer'
            }}
            disabled={loadingLoadData}
          >
            {loadingLoadData ? 'LOADING...' : 'LOAD DATA'}
          </button>
          <button
            onClick={handleMint}
            style={{
              ...buttonStyle,
              opacity: loadingMint ? 0.6 : 1,
              cursor: loadingMint ? 'not-allowed' : 'pointer'
            }}
            disabled={loadingMint}
          >
            {loadingMint ? 'Minting...' : 'MINT'}
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>
          Codebase Licensing Processor Address <span style={{ color: 'red' }}>*</span>
        </label>
        <input
          value={formData.nftMarketPlaceAddress}
          onChange={handleInputChange('nftMarketPlaceAddress')}
          placeholder="Enter licensing processor address"
          style={{ ...inputStyle, fontFamily: 'monospace' }}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>
          Production Partner Address
        </label>
        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
          <input
            value={formData.productionPartnerAddress}
            onChange={handleInputChange('productionPartnerAddress')}
            placeholder="Enter production partner address"
            style={{ ...inputStyle, fontFamily: 'monospace', flex: 1 }}
          />
          <button onClick={handleIssueNFT} style={buttonStyle}>ISSUE NFT</button>
          <button onClick={handleRevoke} style={buttonStyle}>REVOKE</button>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>
          Token ID <span style={{ color: 'red' }}>*</span>
        </label>
        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
          <input
            value={formData.tokenId}
            onChange={handleInputChange('tokenId')}
            placeholder="Enter token ID"
            style={{ ...inputStyle, flex: 1 }}
          />
          <button
            onClick={handleCompleteTransaction}
            style={{
              ...buttonStyle,
              opacity: loadingCompleteTransaction ? 0.6 : 1,
              cursor: loadingCompleteTransaction ? 'not-allowed' : 'pointer'
            }}
            disabled={loadingCompleteTransaction}
          >
            {loadingCompleteTransaction ? 'PROCESSING...' : 'COMPLETE TRANSACTION'}
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>
          Codebase Title <span style={{ color: 'red' }}>*</span>
        </label>
        <input
          value={formData.nftTitle}
          onChange={handleInputChange('nftTitle')}
          placeholder="Enter title"
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>
          Codebase Description
        </label>
        <textarea
          value={formData.nftDescription}
          onChange={handleInputChange('nftDescription')}
          placeholder="Enter description"
          rows={3}
          style={inputStyle}
        />
      </div>

      {/* Supported Currencies and Prices */}
      <div style={{ marginTop: 32, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>Supported currencies and prices</h3>
          <button onClick={handleAddCurrencyRow} style={buttonStyle}>
            Add new currency
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #ddd', backgroundColor: '#f5f5f5' }}>
                <th style={{ textAlign: 'left', padding: 8 }}>Currency</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Address</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Price</th>
                <th style={{ textAlign: 'right', padding: 8 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(currencyRows || [])
                .filter((r) => !r.deleted)
                .map((row, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: 8 }}>
                      {row.symbol ||
                        (row.address?.toLowerCase() === '0x0000000000000000000000000000000000000000'
                          ? daoInfo?.chainId === 56
                            ? 'BNB'
                            : daoInfo?.chainId === 137 || daoInfo?.chainId === 80001
                            ? 'MATIC'
                            : daoInfo?.chainId === 7001
                            ? 'ZETA'
                            : 'ETH'
                          : '')}
                    </td>
                    <td style={{ padding: 8, fontFamily: 'monospace', fontSize: '12px' }}>
                      {row.isEditing ? (
                        <input
                          value={row.address}
                          onChange={(e) => handleRowAddressChange(idx, e.target.value)}
                          placeholder="0x... or 0x0000000000000000000000000000000000000000"
                          style={{ ...inputStyle, fontFamily: 'monospace', width: '100%', marginTop: 0 }}
                        />
                      ) : (
                        <span>{row.address}</span>
                      )}
                      {row.errors?.address && (
                        <div style={{ color: 'red', fontSize: '12px', marginTop: 4 }}>{row.errors.address}</div>
                      )}
                    </td>
                    <td style={{ padding: 8 }}>
                      {row.isEditing ? (
                        <input
                          value={row.price}
                          onChange={(e) => handleRowPriceChange(idx, e.target.value)}
                          placeholder="e.g. 0.1"
                          style={{ ...inputStyle, width: '100%', marginTop: 0 }}
                        />
                      ) : (
                        <span>{row.price || '0'}</span>
                      )}
                      {row.errors?.price && (
                        <div style={{ color: 'red', fontSize: '12px', marginTop: 4 }}>{row.errors.price}</div>
                      )}
                    </td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      {row.isEditing ? (
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => handleRowSave(idx)}
                            disabled={row.loadingSave}
                            style={{
                              ...buttonStyle,
                              fontSize: '12px',
                              padding: '4px 8px',
                              background: '#4caf50',
                              color: 'white',
                              border: 'none'
                            }}
                          >
                            {row.loadingSave ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => handleRowCancel(idx)}
                            style={{ ...buttonStyle, fontSize: '12px', padding: '4px 8px' }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => handleRowEdit(idx)}
                            style={{ ...buttonStyle, fontSize: '12px', padding: '4px 8px' }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleRowDeleteClick(idx)}
                            disabled={row.loadingDelete}
                            style={{
                              ...buttonStyle,
                              fontSize: '12px',
                              padding: '4px 8px',
                              background: '#f44336',
                              color: 'white',
                              border: 'none'
                            }}
                          >
                            {row.loadingDelete ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              {currencyRows.filter((r) => !r.deleted).length === 0 && (
                <tr>
                  <td colSpan={4} style={{ padding: 16, textAlign: 'center', color: '#666' }}>
                    No currencies found. Click "Add new currency" to add one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirm Delete Currency Modal */}
      {confirmDeleteOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: 24,
            borderRadius: 8,
            minWidth: 400
          }}>
            <h3 style={{ marginTop: 0 }}>Confirm deletion</h3>
            <p>Are you sure you want to remove this currency?</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmDeleteOpen(false)}
                disabled={confirmDeleteLoading}
                style={buttonStyle}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={confirmDeleteLoading}
                style={{
                  ...buttonStyle,
                  background: '#f44336',
                  color: 'white',
                  border: 'none'
                }}
              >
                {confirmDeleteLoading ? 'Deleting...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revenue Sharing Configuration */}
      <div style={{ marginTop: 32, marginBottom: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 8 }}>
        <h3 style={{ marginTop: 0, marginBottom: 16 }}>Revenue Sharing Configuration</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          <div>
            <label style={labelStyle}>Current Dev %</label>
            <input
              type="number"
              value={currentDevPartnerPercentage}
              onChange={(e) => setCurrentDevPartnerPercentage(Number(e.target.value || 0))}
              style={inputStyle}
              min="0"
              max="100"
            />
          </div>
          <div>
            <label style={labelStyle}>Old Dev %</label>
            <input
              type="number"
              value={oldDevPartnerPercentage}
              onChange={(e) => setOldDevPartnerPercentage(Number(e.target.value || 0))}
              style={inputStyle}
              min="0"
              max="100"
            />
          </div>
          <div>
            <label style={labelStyle}>LP Holders %</label>
            <input
              type="number"
              value={lpHolderPercentage}
              onChange={(e) => setLpHolderPercentage(Number(e.target.value || 0))}
              style={inputStyle}
              min="0"
              max="100"
            />
          </div>
          <div>
            <label style={labelStyle}>DAO Members %</label>
            <input
              type="number"
              value={daoMembersPercentage}
              onChange={(e) => setDaoMembersPercentage(Number(e.target.value || 0))}
              style={inputStyle}
              min="0"
              max="100"
            />
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <button
            onClick={() => updateRevenuePercentagesOnChain(
              Number(currentDevPartnerPercentage),
              Number(oldDevPartnerPercentage),
              Number(lpHolderPercentage),
              Number(daoMembersPercentage)
            )}
            disabled={updatingRevenue}
            style={{
              ...buttonStyle,
              background: updatingRevenue ? '#ccc' : '#1976d2',
              color: 'white',
              border: 'none'
            }}
          >
            {updatingRevenue ? 'Updating...' : 'Update Percentages'}
          </button>
        </div>
      </div>

      {/* Completion Time Configuration */}
      <div style={{ marginTop: 32, marginBottom: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 8 }}>
        <h3 style={{ marginTop: 0, marginBottom: 16 }}>Completion Time Configuration</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 260px' }}>
            <label style={labelStyle}>Completion Time (Days)</label>
            <input
              type="number"
              value={completionTimeDays}
              onChange={(e) => setCompletionTimeDays(Number(e.target.value || 0))}
              style={inputStyle}
              min="1"
            />
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginTop: 8 }}>
              <input
                type="checkbox"
                checked={autoCompletionEnabled}
                onChange={(e) => setAutoCompletionEnabled(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              <span>Automatically complete transactions after X days</span>
            </label>
          </div>
          <div style={{ flex: '1 1 200px', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
            <button
              onClick={() => updateCompletionTimeOnChain(completionTimeDays)}
              disabled={updatingCompletionTime}
              style={{
                ...buttonStyle,
                background: updatingCompletionTime ? '#ccc' : '#1976d2',
                color: 'white',
                border: 'none',
                marginTop: 32
              }}
            >
              {updatingCompletionTime ? 'Updating...' : 'Update Completion Config'}
            </button>
          </div>
        </div>
        <p style={{ marginTop: 12, fontSize: '12px', color: '#666' }}>
          If auto completion is disabled, transactions will only be considered completed when an admin triggers
          &quot;Complete Transaction&quot;, even after X days.
        </p>
      </div>

      {/* Dev Partners Management */}
      <div style={{ marginTop: 32, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>Dev Partners Management</h3>
          <button onClick={handleAddDevPartner} style={buttonStyle}>
            Add Dev Partner
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #ddd', backgroundColor: '#f5f5f5' }}>
                <th style={{ textAlign: 'left', padding: 8 }}>Address</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Type</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Date Added</th>
                <th style={{ textAlign: 'right', padding: 8 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {devPartners
                .filter((partner) => partner.isActive)
                .map((partner, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: 8, fontFamily: 'monospace', fontSize: '12px' }}>
                      {partner.wallet}
                    </td>
                    <td style={{ padding: 8 }}>
                      <span style={{ color: partner.isCurrent ? '#1976d2' : '#666' }}>
                        {partner.isCurrent ? 'Current' : 'Old'}
                      </span>
                    </td>
                    <td style={{ padding: 8 }}>
                      {new Date(Number(partner.addedTimestamp) * 1000).toLocaleDateString()}
                    </td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        {!partner.isCurrent && (
                          <button
                            onClick={() => handleSetCurrentDevPartner(partner.wallet)}
                            disabled={settingCurrentLoading === partner.wallet}
                            style={{ ...buttonStyle, fontSize: '12px', padding: '4px 8px' }}
                          >
                            {settingCurrentLoading === partner.wallet ? 'Setting...' : 'Set Current'}
                          </button>
                        )}
                        <button
                          onClick={() => handleEditDevPartner(partner)}
                          style={{ ...buttonStyle, fontSize: '12px', padding: '4px 8px' }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteDevPartner(partner.wallet)}
                          disabled={partner.isCurrent}
                          style={{
                            ...buttonStyle,
                            fontSize: '12px',
                            padding: '4px 8px',
                            background: '#f44336',
                            color: 'white',
                            border: 'none',
                            opacity: partner.isCurrent ? 0.5 : 1,
                            cursor: partner.isCurrent ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              {devPartners.filter((partner) => partner.isActive).length === 0 && (
                <tr>
                  <td colSpan={4} style={{ padding: 16, textAlign: 'center', color: '#666' }}>
                    No dev partners found. Click "Add Dev Partner" to add one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dev Partner Form Modal */}
      {devPartnerFormOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: 24,
            borderRadius: 8,
            minWidth: 400,
            maxWidth: 600
          }}>
            <h3 style={{ marginTop: 0 }}>{editingDevPartner ? 'Edit Dev Partner' : 'Add Dev Partner'}</h3>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Dev Partner Address</label>
              <input
                value={devPartnerFormData.address}
                onChange={(e) => setDevPartnerFormData({ ...devPartnerFormData, address: e.target.value })}
                placeholder="0x..."
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={devPartnerFormData.isCurrent}
                  onChange={(e) => setDevPartnerFormData({ ...devPartnerFormData, isCurrent: e.target.checked })}
                  style={{ marginRight: 8 }}
                />
                <span>Set as Current Dev Partner</span>
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDevPartnerFormOpen(false)}
                disabled={devPartnerFormLoading}
                style={buttonStyle}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveDevPartner}
                disabled={devPartnerFormLoading}
                style={{
                  ...buttonStyle,
                  background: '#1976d2',
                  color: 'white',
                  border: 'none'
                }}
              >
                {devPartnerFormLoading ? 'Saving...' : editingDevPartner ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Dev Partner Modal */}
      {confirmDeleteDevPartnerOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: 24,
            borderRadius: 8,
            minWidth: 400
          }}>
            <h3 style={{ marginTop: 0 }}>Confirm deletion</h3>
            <p>Are you sure you want to remove this dev partner?</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmDeleteDevPartnerOpen(false)}
                disabled={confirmDeleteDevPartnerLoading}
                style={buttonStyle}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeleteDevPartner}
                disabled={confirmDeleteDevPartnerLoading}
                style={{
                  ...buttonStyle,
                  background: '#f44336',
                  color: 'white',
                  border: 'none'
                }}
              >
                {confirmDeleteDevPartnerLoading ? 'Deleting...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 24 }}>
        <button
          onClick={onSubmit}
          style={{
            padding: '8px 16px',
            background: '#4caf50',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: loadingSave ? 'not-allowed' : 'pointer',
            opacity: loadingSave ? 0.6 : 1
          }}
          disabled={loadingSave}
        >
          {loadingSave ? 'SAVING...' : 'SAVE'}
        </button>
      </div>
    </div>
  );
};

export default NFTLicensesTab;
